/**
 * Permission Context
 *
 * Provides RBAC permission management for the frontend:
 * - Loads user's effective permissions from backend
 * - Provides permission checking functions
 * - Caches permissions for performance
 * - Auto-refreshes on user change
 */

import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface PermissionContextType {
  permissions: string[];
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

/**
 * Permission Provider Component
 * Wraps the app to provide permission context
 */
export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Load user permissions from backend
   */
  const loadPermissions = async () => {
    // Wait for auth to finish loading before attempting to load permissions
    // Note: The effect re-runs when authLoading changes, so loading state will resolve
    if (authLoading) {
      return;
    }

    if (!user || !isAuthenticated) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Destructure data from axios response - the actual payload is in response.data
      const { data } = await api.get(`/admin/permissions/users/${user.id}`);

      // Type the response data
      const responseData = data as {
        success?: boolean;
        permissions?: Array<{ permission_key: string }>;
      };

      if (responseData.permissions && Array.isArray(responseData.permissions)) {
        // Extract permission keys from response
        const permissionKeys = responseData.permissions.map(
          (p: { permission_key: string }) => p.permission_key
        );
        setPermissions(permissionKeys);
        console.log(`Loaded ${permissionKeys.length} permissions for user ${user.id}`);
      } else {
        console.warn('No permissions array in response:', responseData);
        setPermissions([]);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh permissions when user changes or auth finishes loading
   */
  useEffect(() => {
    loadPermissions();
  }, [user?.id, isAuthenticated, authLoading]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = (permissionsToCheck: string[]): boolean => {
    return permissionsToCheck.some(permission => permissions.includes(permission));
  };

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = (permissionsToCheck: string[]): boolean => {
    return permissionsToCheck.every(permission => permissions.includes(permission));
  };

  /**
   * Manually refresh permissions (e.g., after permission change)
   */
  const refreshPermissions = async () => {
    await loadPermissions();
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        loading,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to access permission context
 * Must be used within PermissionProvider
 */
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
